import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { InputTextarea } from "primereact/inputtextarea";
import { BsMic } from "react-icons/bs";
import { AiOutlineStop } from "react-icons/ai";
import { ProgressSpinner } from "primereact/progressspinner";
import ReactMarkdown from "react-markdown";
import type { Node, Edge } from "@xyflow/react";
import { nanoid } from "nanoid/non-secure";
import { NodeData } from "../types/types";
import { useStore } from "../App/store";
import { buildSystemPromptWithContext } from "../utils/aiContextBuilder";
import {
  parseTextStructure,
  parseIndentedOutline,
} from "../utils/textStructureParser";
import { calculateChildNodePositions } from "../utils/aiResponseParser";

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface AIInputFloatProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (text: string) => void;
  nodes: Node<NodeData>[];
  edges: Edge[];
  selectedNodeId: string | null;
  onUpdateNodes?: (
    nodes: Node<NodeData>[],
    edges: Edge[],
    deleteNodeIds?: string[],
    selectNodeId?: string | null,
    formatAll?: boolean,
  ) => void;
  aiModel?: string;
  context?: "map" | "notes";
  onNotesResponse?: (
    content: string,
    action?: "add" | "overwrite" | "append",
  ) => void;
}

const AIInputFloat: React.FC<AIInputFloatProps> = ({
  visible,
  onClose,
  onSubmit,
  nodes,
  edges,
  selectedNodeId,
  onUpdateNodes,
  aiModel = "gpt-4o-mini",
  context = "map",
  onNotesResponse,
}) => {
  const [inputText, setInputText] = useState("");
  const [chat, setChat] = useState<{ role: "user" | "ai"; text: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const stopStreamingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [pendingNotesContent, setPendingNotesContent] = useState<string | null>(
    null,
  );

  // Backend API base URL - Always use cloud functions
  const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.MODE === "dev"
      ? "https://api-se7ho5yqsa-uc.a.run.app"
      : "https://api-deqxfy6vga-uc.a.run.app");

  useEffect(() => {
    if (visible) {
      // Clear chat history and input when showing the float
      setChat([]);
      setInputText("");
      setLoading(false);
      setRecording(false);
      stopStreamingRef.current = false;

      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [visible]);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [chat, loading]);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = "da-DK"; // Danish language
      recognitionRef.current.continuous = true; // Keep listening for longer
      recognitionRef.current.interimResults = true; // Show interim results
      recognitionRef.current.maxAlternatives = 1;

      let finalTranscript = "";
      let silenceTimer: NodeJS.Timeout | null = null;

      recognitionRef.current.onresult = (event: any) => {
        let interimTranscript = "";

        // Process all results
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
            console.log("Final speech result:", transcript);
          } else {
            interimTranscript += transcript;
            console.log("Interim speech result:", transcript);
          }
        }

        // Update input with current transcript
        const currentText = (finalTranscript + interimTranscript).trim();
        if (currentText) {
          setInputText(currentText);
        }

        // Reset silence timer on new speech
        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }

        // Wait for 2 seconds of silence before auto-submitting
        silenceTimer = setTimeout(() => {
          if (finalTranscript.trim() && !loading) {
            console.log(
              "Auto-submitting after silence:",
              finalTranscript.trim(),
            );
            const textToSubmit = finalTranscript.trim();
            finalTranscript = "";
            setInputText("");
            handleSendVoice(textToSubmit);
            // Note: Don't stop recognition, keep mic open
          }
        }, 2000); // Wait 2 seconds of silence
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error("Speech recognition error:", event.error);
        if (event.error === "no-speech") {
          console.log("No speech detected");
        } else if (event.error === "not-allowed") {
          alert(
            "Microphone adgang nægtet. Tillad mikrofonadgang i browserindstillingerne.",
          );
        }
        setRecording(false);
        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }
      };

      recognitionRef.current.onend = () => {
        console.log("Speech recognition ended");
        // Only set recording to false if user manually stopped it
        // Otherwise, restart automatically to keep mic open
        if (recognitionRef.current && recording) {
          console.log("Restarting speech recognition to keep mic open");
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.error("Failed to restart recognition:", e);
            setRecording(false);
          }
        } else {
          setRecording(false);
        }
        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }
      };

      recognitionRef.current.onstart = () => {
        console.log("Speech recognition started");
      };
    } else {
      console.warn("Speech recognition not supported in this browser");
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Allow dragging from the drag handle or the container margins/borders
    // Prevent dragging if user is clicking on interactive elements
    const target = e.target as HTMLElement;

    // Don't start drag if clicking on interactive elements
    if (
      target.tagName === "TEXTAREA" ||
      target.tagName === "BUTTON" ||
      target.tagName === "INPUT" ||
      target.closest(".ai-input-wrapper") ||
      target.closest(".ai-input-icon") ||
      target.closest(".ai-chat-history")
    ) {
      return;
    }

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  // Toggle voice recording
  const toggleRecording = () => {
    if (!recognitionRef.current) {
      console.warn("Speech recognition not supported");
      return;
    }

    if (recording) {
      recognitionRef.current.stop();
      setRecording(false);
    } else {
      recognitionRef.current.start();
      setRecording(true);
    }
  };

  // Helper function to estimate request complexity and determine max_tokens
  const estimateMaxTokens = (message: string, nodeCount: number): number => {
    // Extract all numbers from the message and look for multiplication patterns
    const numbers = message.match(/\b(\d+)\b/g);
    let requestedNodes = 3; // Default

    if (numbers && numbers.length > 0) {
      const nums = numbers.map((n) => parseInt(n));

      // Look for patterns like "3 nodes + 3 subnodes" or "3 med 3 hver"
      if (nums.length >= 2) {
        // If we see multiple numbers, multiply them (e.g., 3 nodes with 3 each = 9 + 3 = 12)
        requestedNodes = nums[0] * (1 + nums[1]);
      } else {
        requestedNodes = nums[0];
      }

      // Look for keywords that suggest more nodes
      const lowerMsg = message.toLowerCase();
      if (
        lowerMsg.includes("sub") ||
        lowerMsg.includes("each") ||
        lowerMsg.includes("hver")
      ) {
        // Double the estimate if subnodes are mentioned
        requestedNodes = requestedNodes * 1.5;
      }
    }

    console.log(`Estimated ${requestedNodes} nodes from request: "${message}"`);

    // Estimate tokens needed per node (including structure)
    const tokensPerNode = 180; // Increased from 150
    const baseTokens = 1500; // Increased base for conversational response
    const estimated = baseTokens + Math.ceil(requestedNodes) * tokensPerNode;

    // Cap based on model capabilities
    const isClaudeModel = aiModel.startsWith("claude");
    const maxAllowed = isClaudeModel ? 8000 : 16000;

    const finalTokens = Math.min(estimated, maxAllowed);
    console.log(
      `Allocating ${finalTokens} tokens (estimated ${estimated}, max ${maxAllowed})`,
    );

    return finalTokens;
  };

  // Helper function to detect if request is too large and needs batching
  const shouldBatchRequest = (
    message: string,
  ): { shouldBatch: boolean; nodeCount: number } => {
    const numbers = message.match(/\b(\d+)\b/g);
    let requestedNodes = 0;

    if (numbers && numbers.length > 0) {
      const nums = numbers.map((n) => parseInt(n));

      // Look for patterns like "3 nodes + 3 subnodes" or "3 med 3 hver"
      if (nums.length >= 2) {
        requestedNodes = nums[0] * (1 + nums[1]);
      } else {
        requestedNodes = nums[0];
      }

      const lowerMsg = message.toLowerCase();
      if (
        lowerMsg.includes("sub") ||
        lowerMsg.includes("each") ||
        lowerMsg.includes("hver")
      ) {
        requestedNodes = Math.ceil(requestedNodes * 1.5);
      }
    }

    // SAFETY: If calculation results in absurd numbers (>100), it's likely an error
    // The user probably pasted content, not requesting thousands of nodes
    const MAX_REASONABLE_NODES = 25;
    if (requestedNodes > MAX_REASONABLE_NODES) {
      console.warn(
        `Detected unrealistic node count (${requestedNodes}). Treating as content to structure, not a quantity request.`,
      );
      return { shouldBatch: false, nodeCount: 0 }; // Let AI handle it normally
    }

    // Batch if requesting more than 15 nodes at once
    return { shouldBatch: requestedNodes > 15, nodeCount: requestedNodes };
  };

  // Streamed OpenAI call via backend
  const askOpenAI = async (
    text: string,
    onChunk: (chunk: string) => void,
    chatHistory: Array<{ role: string; content: string }>,
    maxTokens?: number,
  ): Promise<void> => {
    try {
      setStreaming(true);

      // Build context-aware system prompt
      const systemPrompt = buildSystemPromptWithContext(
        nodes,
        edges,
        selectedNodeId,
        chatHistory,
      );

      // Calculate dynamic max_tokens if not provided
      const dynamicMaxTokens =
        maxTokens || estimateMaxTokens(text, nodes.length);

      console.log("🤖 AI Request Details:");
      console.log("  Model:", aiModel);
      console.log("  Max Tokens:", dynamicMaxTokens);
      console.log("  Message:", text);

      const response = await fetch(`${API_BASE_URL}/brainstorm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: text,
          systemPrompt,
          chatHistory,
          stream: true,
          model: aiModel,
          maxTokens: dynamicMaxTokens,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("No response body");
      }

      while (true) {
        if (stopStreamingRef.current) {
          await reader.cancel();
          break;
        }

        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content || "";
              if (content) {
                onChunk(content);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      console.error("Error in askOpenAI:", error);
      onChunk("\n[Error: " + ((error as any).message || "Unknown error") + "]");
    } finally {
      setStreaming(false);
    }
  };

  type FocusFlowGraph = {
    nodes: Array<{
      id: string;
      position: { x: number; y: number };
      data?: Record<string, any>;
      parentId?: string;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      type: "reference";
    }>;
    viewport?: { x: number; y: number; zoom: number };
  };

  const buildNodeDataFromFocusFlow = (
    node: FocusFlowGraph["nodes"][number],
  ): NodeData => {
    const extractTitle = (data: Record<string, any> | undefined): string => {
      if (!data) return "";
      const candidates = [data.label, data.title, data.name, data.text];
      for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length) {
          return candidate.trim();
        }
      }
      return "";
    };

    const label = extractTitle(node.data) || node.id;

    const notes =
      typeof node.data?.notes === "string"
        ? node.data.notes
        : typeof node.data?.description === "string"
          ? node.data.description
          : "";

    return {
      label,
      icon: "",
      object: "",
      objectType: "text",
      objectWidth: 50,
      objectHeight: 18,
      notes,
      complete: false,
      status: "none",
      flagged: "none",
      color: "black",
      backgroundColor: "#ffffff",
      presentationOrder: "",
      priority: "",
      links: [],
      value: "0",
      values: [],
      rollupValues: [],
      customProperties: [],
      event: {} as any,
      address: {} as any,
      task: {} as any,
      risk: undefined,
      decision: undefined,
      ms365Document: {} as any,
      why: undefined,
      type: undefined,
      resource: undefined,
    };
  };

  const adaptFocusFlowGraphToFocusMap = (
    graph: FocusFlowGraph,
    attachParentId: string,
    options?: {
      seedNodes?: Node<NodeData>[];
      prefixChildOrder?: boolean;
    },
  ): { nodes: Node<NodeData>[]; edges: Edge[] } => {
    const seedNodes = options?.seedNodes || nodes;
    const idMap = new Map<string, string>();
    const mappedNodes: Array<{
      id: string;
      parentId?: string;
      abs: { x: number; y: number };
      data: NodeData;
    }> = [];

    // Engine guarantees parent-before-child ordering; we preserve that.
    for (const node of graph.nodes) {
      const newId = nanoid();
      idMap.set(node.id, newId);
      mappedNodes.push({
        id: newId,
        parentId: node.parentId,
        abs: node.position,
        data: buildNodeDataFromFocusFlow(node),
      });
    }

    const parentIdByNewId = new Map<string, string | undefined>();
    for (const node of mappedNodes) {
      const mappedParent = node.parentId ? idMap.get(node.parentId) : undefined;
      parentIdByNewId.set(node.id, mappedParent);
    }

    // Attach top-level nodes under the selected node (or center node).
    for (const node of mappedNodes) {
      if (!parentIdByNewId.get(node.id) && attachParentId) {
        parentIdByNewId.set(node.id, attachParentId);
      }
    }

    const stripLeadingOrder = (text: string): string => {
      // Remove leading numbering like "1.", "1:", "(1)", "#1:", etc.
      return text.replace(/^\s*(?:#?\(?\d+\)?[\.:\)]\s+|#\s*\d+\s*:\s*)/i, "");
    };

    if (options?.prefixChildOrder && attachParentId) {
      const directChildren = mappedNodes.filter(
        (n) => parentIdByNewId.get(n.id) === attachParentId,
      );
      directChildren.forEach((child, index) => {
        const baseTitle = stripLeadingOrder(child.data.label || "");
        child.data = {
          ...child.data,
          label: `${index + 1}: ${baseTitle || child.data.label}`,
        };
      });
    }

    const absById = new Map<string, { x: number; y: number }>();
    // Seed with existing nodes so relative calculations work for attachment parents.
    for (const existing of seedNodes) {
      const abs =
        (existing as any)?.internals?.positionAbsolute || existing.position;
      absById.set(existing.id, abs);
    }

    const focusMapNodes: Node<NodeData>[] = [];
    for (const node of mappedNodes) {
      const parentId = parentIdByNewId.get(node.id);
      const parentAbs = parentId ? absById.get(parentId) : undefined;
      const position = parentAbs
        ? { x: node.abs.x - parentAbs.x, y: node.abs.y - parentAbs.y }
        : { x: node.abs.x, y: node.abs.y };

      const focusNode: Node<NodeData> = {
        id: node.id,
        type: "mindmap",
        parentId: parentId || undefined,
        position,
        data: node.data,
        width: node.data.objectWidth,
        height: node.data.objectHeight,
        dragHandle: ".dragHandle",
        style: { backgroundColor: "#fff" },
        selected: false,
        dragging: false,
      };

      focusMapNodes.push(focusNode);
      absById.set(node.id, node.abs);
    }

    // Hierarchy edges are derived from parentId for rendering.
    const hierarchyPairs = new Set<string>();
    const hierarchyEdges: Edge[] = [];
    for (const node of focusMapNodes) {
      const parentId = node.parentId;
      if (!parentId) continue;
      const parentNode = seedNodes.find((n) => n.id === parentId);
      const edgeType = parentNode?.type === "center" ? "center" : "mindmap";
      hierarchyPairs.add(`${parentId}::${node.id}`);
      hierarchyEdges.push({
        id: nanoid(),
        source: parentId,
        target: node.id,
        type: edgeType,
      });
    }

    return {
      nodes: focusMapNodes,
      edges: hierarchyEdges,
    };
  };

  const getFocusFlowGraphRootNode = (
    graph: FocusFlowGraph,
  ): FocusFlowGraph["nodes"][number] | undefined => {
    const rootCandidates = graph.nodes.filter((n) => !n.parentId);
    if (rootCandidates.length === 0) return undefined;
    // Engine order is typically parent-before-child; keep it stable.
    return rootCandidates[0];
  };

  const stripRootFromFocusFlowGraph = (
    graph: FocusFlowGraph,
    rootId: string,
  ): FocusFlowGraph => {
    const nodesWithoutRoot = graph.nodes
      .filter((n) => n.id !== rootId)
      .map((n) => {
        if (n.parentId === rootId) {
          const { parentId, ...rest } = n;
          return rest;
        }
        return n;
      });

    const edgesWithoutRoot = graph.edges.filter(
      (e) => e.source !== rootId && e.target !== rootId,
    );

    return {
      ...graph,
      nodes: nodesWithoutRoot,
      edges: edgesWithoutRoot,
    };
  };

  const translateFocusFlowGraph = (
    graph: FocusFlowGraph,
    dx: number,
    dy: number,
  ): FocusFlowGraph => {
    return {
      ...graph,
      nodes: graph.nodes.map((n) => ({
        ...n,
        position: { x: n.position.x + dx, y: n.position.y + dy },
      })),
    };
  };

  const buildCenterNodeDataFromFocusFlow = (
    rootNode: FocusFlowGraph["nodes"][number] | undefined,
    fallbackLabel: string,
  ): NodeData => {
    const base = rootNode ? buildNodeDataFromFocusFlow(rootNode) : null;

    return {
      ...(base ||
        buildNodeDataFromFocusFlow({
          id: fallbackLabel,
          position: { x: 0, y: 0 },
          data: { label: fallbackLabel, notes: "" },
        })),
      label: (base?.label || fallbackLabel) as string,
      objectWidth: 200,
      objectHeight: 200,
      objectType: "",
    };
  };

  const createUnboundGoalCenterNode = (data: NodeData): Node<NodeData> => {
    const absPositions = nodes.map(
      (n) => (n as any)?.internals?.positionAbsolute || n.position,
    );
    const maxX = absPositions.length
      ? Math.max(...absPositions.map((p) => p.x))
      : 0;
    const avgY = absPositions.length
      ? absPositions.reduce((sum, p) => sum + p.y, 0) / absPositions.length
      : 0;

    const positionAbsolute = {
      x: maxX + 600,
      y: avgY,
    };

    return {
      id: nanoid(),
      type: "center",
      data: {
        ...data,
        event: (data.event || ({} as any)) as any,
        address: (data.address || ({} as any)) as any,
        task: (data.task || ({} as any)) as any,
        ms365Document: (data.ms365Document || ({} as any)) as any,
      },
      style: { backgroundColor: "#fff" },
      position: { x: positionAbsolute.x, y: positionAbsolute.y },
      dragHandle: ".dragHandle",
      dragging: false,
      selected: false,
      width: 200,
      height: 100,
      parentId: undefined,
    };
  };

  const buildExistingNodeUpdateWithCenterData = (
    targetNodeId: string,
    centerData: NodeData,
  ): Node<NodeData> | null => {
    const existing = nodes.find((n) => n.id === targetNodeId);
    if (!existing) return null;

    return {
      ...existing,
      data: {
        ...existing.data,
        label: centerData.label,
        notes: centerData.notes,
        why: centerData.why ?? existing.data.why,
      },
    };
  };

  const callFocusFlowEngineCreate = async (
    text: string,
  ): Promise<{ graph: FocusFlowGraph; explanation?: string }> => {
    // Try to extract requested node count for better token estimation
    // Matches: "50 noder", "100 nodes", "tredive knuder" etc.
    const nodeMatch = text.match(/(\d+)\s*(noder?|nodes?|knuder?)/i);
    const requestedNodes = nodeMatch ? parseInt(nodeMatch[1]) : undefined;

    console.log("🔧 Sending request:", { text, requestedNodes });

    const res = await fetch(`${API_BASE_URL}/ff/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        text,
        ...(requestedNodes ? { requestedNodes } : {}),
      }),
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      throw new Error(errText || `HTTP error! status: ${res.status}`);
    }

    const json = (await res.json()) as {
      graph?: FocusFlowGraph;
      explanation?: string;
      error?: string;
    };
    if (json.error) throw new Error(json.error);
    if (!json.graph)
      throw new Error("Missing graph in FocusFlowEngine response");
    return { graph: json.graph, explanation: json.explanation };
  };

  const callFocusFlowEngineModify = async (
    text: string,
    graph: FocusFlowGraph,
  ): Promise<{ graph: FocusFlowGraph; explanation?: string }> => {
    const res = await fetch(`${API_BASE_URL}/ff/modify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text, graph }),
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      throw new Error(errText || `HTTP error! status: ${res.status}`);
    }

    const json = (await res.json()) as {
      graph?: FocusFlowGraph;
      explanation?: string;
      error?: string;
    };
    if (json.error) throw new Error(json.error);
    if (!json.graph)
      throw new Error("Missing graph in FocusFlowEngine response");
    return { graph: json.graph, explanation: json.explanation };
  };

  const exportFocusMapToFocusFlowGraph = (
    currentNodes: Node<NodeData>[],
    currentEdges: Edge[],
  ): FocusFlowGraph => {
    const focusFlowNodes: FocusFlowGraph["nodes"] = currentNodes.map((n) => {
      const data: Record<string, any> = {
        label: n.data?.label,
        notes: n.data?.notes,
      };

      const base: FocusFlowGraph["nodes"][number] = {
        id: n.id,
        position: { x: n.position.x, y: n.position.y },
        data,
      };

      // Option A: hierarchy is defined by parentId; for engine, omit empty string.
      if (n.parentId) base.parentId = n.parentId;
      return base;
    });

    const focusFlowEdges: FocusFlowGraph["edges"] = currentEdges
      .filter((e) => e.type === "reference")
      .map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        type: "reference" as const,
      }));

    return { nodes: focusFlowNodes, edges: focusFlowEdges };
  };

  const shouldUseModifyForRequest = (text: string): boolean => {
    // If we only have the center node, default to create.
    if (nodes.length <= 1) return false;

    return /\b(modify|update|change|edit|restructure|refactor|omstruktur|ændr|rediger|opdater)\b/i.test(
      text,
    );
  };

  const shouldPrefixChildOrderForRequest = (text: string): boolean => {
    // Only when user explicitly asks for ordering/numbering.
    return /\b(?:nummer|nummering|nummerering|rækkefølge|number(?:ing)?|ordered|order|sequence)\b/i.test(
      text,
    );
  };

  const isQuestionRequest = (text: string): boolean => {
    // Check if user is asking a question rather than requesting node generation/modification
    // Look for question patterns in multiple languages
    const questionPatterns = [
      /^(hvad|hvordan|hvorfor|hvornår|hvor|hvem|hvilke?n?)\b/i, // Danish question words
      /^(what|how|why|when|where|who|which)\b/i, // English question words
      /\b(forklar|beskriv|fortæl|explain|describe|tell)\b/i, // Request for explanation
      /\?$/, // Ends with question mark
      /\b(kan du|vil du|could you|would you|can you)\b/i, // Polite requests
    ];

    return questionPatterns.some((pattern) => pattern.test(text.trim()));
  };

  const adaptFocusFlowGraphToFocusMapInPlace = (
    graph: FocusFlowGraph,
    existingCenterNodeId: string | undefined,
  ): { nodes: Node<NodeData>[]; edges: Edge[] } => {
    const existingById = new Map(nodes.map((n) => [n.id, n] as const));
    const absById = new Map<string, { x: number; y: number }>();
    for (const existing of nodes) {
      absById.set(
        existing.id,
        (existing as any)?.internals?.positionAbsolute || existing.position,
      );
    }

    const focusNodes: Node<NodeData>[] = [];
    const typeById = new Map<string, string>();

    for (const node of graph.nodes) {
      const existing = existingById.get(node.id);
      const isCenter =
        existing?.type === "center" || node.id === existingCenterNodeId;

      const incomingData = buildNodeDataFromFocusFlow(node);
      const mergedData: NodeData = existing?.data
        ? {
            ...existing.data,
            label: incomingData.label,
            notes: incomingData.notes,
          }
        : incomingData;

      const parentId = isCenter
        ? undefined
        : node.parentId || existing?.parentId || undefined;
      const parentAbs = parentId ? absById.get(parentId) : undefined;

      const abs = (() => {
        if (isCenter && existing) {
          return (
            (existing as any)?.internals?.positionAbsolute || existing.position
          );
        }

        if (parentAbs) {
          return {
            x: parentAbs.x + node.position.x,
            y: parentAbs.y + node.position.y,
          };
        }

        return { x: node.position.x, y: node.position.y };
      })();

      const position = parentAbs
        ? { x: abs.x - parentAbs.x, y: abs.y - parentAbs.y }
        : { x: abs.x, y: abs.y };

      const focusNode: Node<NodeData> = {
        id: node.id,
        type: isCenter ? "center" : existing?.type || "mindmap",
        parentId,
        position,
        data: mergedData,
        width: mergedData.objectWidth,
        height: mergedData.objectHeight,
        dragHandle: ".dragHandle",
        style: existing?.style || { backgroundColor: "#fff" },
        selected: false,
        dragging: false,
      };

      focusNodes.push(focusNode);
      typeById.set(focusNode.id, focusNode.type || "mindmap");
      absById.set(focusNode.id, abs);
    }

    // Hierarchy edges are derived from parentId for rendering.
    const hierarchyPairs = new Set<string>();
    const hierarchyEdges: Edge[] = [];
    for (const node of focusNodes) {
      const parentId = node.parentId;
      if (!parentId) continue;
      const edgeType =
        typeById.get(parentId) === "center" ? "center" : "mindmap";
      hierarchyPairs.add(`${parentId}::${node.id}`);
      hierarchyEdges.push({
        id: nanoid(),
        source: parentId,
        target: node.id,
        type: edgeType,
      });
    }

    return { nodes: focusNodes, edges: hierarchyEdges };
  };

  // Handle sending question with structured outputs (no streaming)
  const handleSend = async () => {
    const question = inputText.trim();
    if (!question) return;

    setChat((prev) => [...prev, { role: "user", text: question }]);
    setInputText("");
    setLoading(true);

    try {
      const chatHistory = chat.map((msg) => ({
        role: msg.role === "user" ? "user" : "assistant",
        content: msg.text,
      }));

      console.log("=== AIInputFloat: handleSend ===");
      console.log("Context mode:", context);
      console.log("Selected node ID:", selectedNodeId);
      console.log("Current nodes count:", nodes.length);

      // Handle notes context differently - just get text response
      if (context === "notes") {
        // Build system prompt for notes context
        const notesSystemPrompt = `You are a helpful assistant working within a notes editor context. Your role is to help users create, edit, and improve their notes.

IMPORTANT INSTRUCTIONS:
- Provide clear, well-formatted text content that is suitable for notes
- Use markdown formatting where appropriate (headings, lists, bold, italic, etc.)
- Keep responses focused and relevant to note-taking
- Provide helpful, concise responses without adding prompts about how to add content to notes
- For follow-up questions or clarifications, provide helpful responses
- If the user asks general questions about the notes, respond conversationally
- DO NOT return JSON or structured data - only return plain text suitable for notes`;

        const response = await fetch(`${API_BASE_URL}/brainstorm`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: question,
            systemPrompt: notesSystemPrompt,
            chatHistory: chatHistory,
            stream: false,
            model: aiModel,
            maxTokens: 2000,
            responseFormat: "text",
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("API Error Response:", errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        let aiText =
          data.response || data.text || data.content || "No response received.";

        // Update chat with response
        setChat((prev) => [...prev, { role: "ai", text: aiText }]);

        // Set this content as pending - user can click buttons to add it
        setPendingNotesContent(aiText);
      } else {
        // Map context: Check if this is a question that should be answered, not a node generation request
        if (isQuestionRequest(question)) {
          // User is asking a question - provide a text answer
          const mapSystemPrompt = `You are a helpful assistant for a mind-mapping application. The user is working on a mind map and has a question.

IMPORTANT INSTRUCTIONS:
- Answer the user's question clearly and helpfully
- Provide context-aware responses based on the mind map content if relevant
- Use markdown formatting where appropriate
- Keep responses focused and concise
- DO NOT generate or suggest node structures unless explicitly asked
- If the user asks about their map, analyze it and provide insights`;

          const response = await fetch(`${API_BASE_URL}/brainstorm`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: question,
              systemPrompt: mapSystemPrompt,
              chatHistory: chatHistory,
              stream: false,
              model: aiModel,
              maxTokens: 2000,
              responseFormat: "text",
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error("API Error Response:", errorText);
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          let aiText =
            data.response ||
            data.text ||
            data.content ||
            "No response received.";

          // Update chat with response
          setChat((prev) => [...prev, { role: "ai", text: aiText }]);
        } else {
          // Not a question - proceed with node generation/modification
          // Map context: use FocusFlowEngine output and apply parentId-based hierarchy.
          const existingCenterNodeId = nodes.find(
            (n) => n.type === "center",
          )?.id;
          const useModify = shouldUseModifyForRequest(question);
          const { graph, explanation } = useModify
            ? await callFocusFlowEngineModify(
                question,
                exportFocusMapToFocusFlowGraph(nodes, edges),
              )
            : await callFocusFlowEngineCreate(question);

          let adapted: { nodes: Node<NodeData>[]; edges: Edge[] };
          let extraNodes: Node<NodeData>[] = [];
          let selectNodeIdFromAI: string | null = null;

          if (useModify) {
            adapted = adaptFocusFlowGraphToFocusMapInPlace(
              graph,
              existingCenterNodeId,
            );
          } else {
            const prefixChildOrder = shouldPrefixChildOrderForRequest(question);
            const rootNode = getFocusFlowGraphRootNode(graph);
            const centerData = buildCenterNodeDataFromFocusFlow(
              rootNode,
              question,
            );

            if (selectedNodeId) {
              // Populate selected node with the AI's center/root data.
              const updatedSelected = buildExistingNodeUpdateWithCenterData(
                selectedNodeId,
                centerData,
              );
              if (updatedSelected) extraNodes.push(updatedSelected);

              // Attach the rest of the structure under the selected node.
              const graphWithoutRoot = rootNode
                ? stripRootFromFocusFlowGraph(graph, rootNode.id)
                : graph;
              adapted = adaptFocusFlowGraphToFocusMap(
                graphWithoutRoot,
                selectedNodeId,
                { prefixChildOrder },
              );
            } else {
              // No selection: create a new unbound Goal/center node and build structure under it.
              const newCenterNode = createUnboundGoalCenterNode(centerData);
              extraNodes.push(newCenterNode);
              selectNodeIdFromAI = newCenterNode.id;

              const graphWithoutRoot = rootNode
                ? stripRootFromFocusFlowGraph(graph, rootNode.id)
                : graph;
              const translatedGraph = translateFocusFlowGraph(
                graphWithoutRoot,
                newCenterNode.position.x,
                newCenterNode.position.y,
              );

              adapted = adaptFocusFlowGraphToFocusMap(
                translatedGraph,
                newCenterNode.id,
                {
                  seedNodes: [...nodes, newCenterNode],
                  prefixChildOrder,
                },
              );
            }
          }

          setChat((prev) => [
            ...prev,
            {
              role: "ai",
              text:
                explanation ||
                `Created ${adapted.nodes.length} nodes and ${adapted.edges.length} edges.`,
            },
          ]);

          if (
            onUpdateNodes &&
            (adapted.nodes.length > 0 || extraNodes.length > 0)
          ) {
            onUpdateNodes(
              [...extraNodes, ...adapted.nodes],
              adapted.edges,
              [],
              selectNodeIdFromAI,
              false,
            );
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSend:", error);
      setChat((prev) => [
        ...prev,
        {
          role: "ai",
          text: `Error: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
        },
      ]);
    } finally {
      setLoading(false);
      console.log("=== End AIInputFloat: handleSend ===");
      console.log("AI Input submitted:", question);

      // Log error to Firestore for debugging
      if (chat[chat.length - 1]?.text.includes("Error")) {
        try {
          const errorLog = {
            timestamp: new Date().toISOString(),
            error: chat[chat.length - 1].text,
            question,
            model: aiModel,
            nodesCount: nodes.length,
          };
          console.log("Error logged to Firestore");
          // TODO: Add actual Firestore logging here if needed
        } catch (logError) {
          console.error("Failed to log error:", logError);
        }
      }

      onSubmit(question);
    }
  };

  // Handle sending voice question (can keep streaming for voice)
  const handleSendVoice = async (voiceText: string) => {
    setChat((prev) => [...prev, { role: "user", text: voiceText }]);
    setLoading(true);
    stopStreamingRef.current = false;

    // Map context: prefer FocusFlowEngine (non-streaming) so we get a valid graph.
    if (context !== "notes") {
      try {
        // Check if this is a question that should be answered, not a node generation request
        if (isQuestionRequest(voiceText)) {
          // User is asking a question - provide a text answer
          const chatHistory = chat.map((msg) => ({
            role: msg.role === "user" ? "user" : "assistant",
            content: msg.text,
          }));

          const mapSystemPrompt = `You are a helpful assistant for a mind-mapping application. The user is working on a mind map and has a question.

IMPORTANT INSTRUCTIONS:
- Answer the user's question clearly and helpfully
- Provide context-aware responses based on the mind map content if relevant
- Use markdown formatting where appropriate
- Keep responses focused and concise
- DO NOT generate or suggest node structures unless explicitly asked
- If the user asks about their map, analyze it and provide insights`;

          const response = await fetch(`${API_BASE_URL}/brainstorm`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: voiceText,
              systemPrompt: mapSystemPrompt,
              chatHistory: chatHistory,
              stream: false,
              model: aiModel,
              maxTokens: 2000,
              responseFormat: "text",
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error("API Error Response:", errorText);
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          let aiText =
            data.response ||
            data.text ||
            data.content ||
            "No response received.";

          // Update chat with response
          setChat((prev) => [...prev, { role: "ai", text: aiText }]);
        } else {
          // Not a question - proceed with node generation/modification
          const existingCenterNodeId = nodes.find(
            (n) => n.type === "center",
          )?.id;
          const useModify = shouldUseModifyForRequest(voiceText);
          const { graph, explanation } = useModify
            ? await callFocusFlowEngineModify(
                voiceText,
                exportFocusMapToFocusFlowGraph(nodes, edges),
              )
            : await callFocusFlowEngineCreate(voiceText);

          let adapted: { nodes: Node<NodeData>[]; edges: Edge[] };
          let extraNodes: Node<NodeData>[] = [];
          let selectNodeIdFromAI: string | null = null;

          if (useModify) {
            adapted = adaptFocusFlowGraphToFocusMapInPlace(
              graph,
              existingCenterNodeId,
            );
          } else {
            const prefixChildOrder =
              shouldPrefixChildOrderForRequest(voiceText);
            const rootNode = getFocusFlowGraphRootNode(graph);
            const centerData = buildCenterNodeDataFromFocusFlow(
              rootNode,
              voiceText,
            );

            if (selectedNodeId) {
              const updatedSelected = buildExistingNodeUpdateWithCenterData(
                selectedNodeId,
                centerData,
              );
              if (updatedSelected) extraNodes.push(updatedSelected);

              const graphWithoutRoot = rootNode
                ? stripRootFromFocusFlowGraph(graph, rootNode.id)
                : graph;
              adapted = adaptFocusFlowGraphToFocusMap(
                graphWithoutRoot,
                selectedNodeId,
                { prefixChildOrder },
              );
            } else {
              const newCenterNode = createUnboundGoalCenterNode(centerData);
              extraNodes.push(newCenterNode);
              selectNodeIdFromAI = newCenterNode.id;

              const graphWithoutRoot = rootNode
                ? stripRootFromFocusFlowGraph(graph, rootNode.id)
                : graph;
              const translatedGraph = translateFocusFlowGraph(
                graphWithoutRoot,
                newCenterNode.position.x,
                newCenterNode.position.y,
              );

              adapted = adaptFocusFlowGraphToFocusMap(
                translatedGraph,
                newCenterNode.id,
                {
                  seedNodes: [...nodes, newCenterNode],
                  prefixChildOrder,
                },
              );
            }
          }

          setChat((prev) => [
            ...prev,
            {
              role: "ai",
              text:
                explanation ||
                `Created ${adapted.nodes.length} nodes and ${adapted.edges.length} edges.`,
            },
          ]);

          if (
            onUpdateNodes &&
            (adapted.nodes.length > 0 || extraNodes.length > 0)
          ) {
            onUpdateNodes(
              [...extraNodes, ...adapted.nodes],
              adapted.edges,
              [],
              selectNodeIdFromAI,
              false,
            );
          }
        }
      } catch (error) {
        console.error("Error in handleSendVoice (FocusFlowEngine):", error);
        setChat((prev) => [
          ...prev,
          {
            role: "ai",
            text: `Error: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          },
        ]);
      } finally {
        setLoading(false);
        onSubmit(voiceText);
      }
      return;
    }

    let aiResponse = "";
    setChat((prev) => [...prev, { role: "ai", text: "" }]);

    // Build chat history
    const chatHistory = chat.map((msg) => ({
      role: msg.role === "user" ? "user" : "assistant",
      content: msg.text,
    }));

    await askOpenAI(
      voiceText,
      (chunk) => {
        aiResponse += chunk;

        // Try to extract and stream just the conversational part
        const responseMatch = aiResponse.match(
          /"response":\s*"([^"]*(?:\\.[^"]*)*)"/s,
        );
        if (responseMatch) {
          const conversationalText = responseMatch[1]
            .replace(/\\n/g, "\n")
            .replace(/\\"/g, '"');
          setChat((prev) => {
            const newChat = [...prev];
            newChat[newChat.length - 1] = {
              role: "ai",
              text: conversationalText,
            };
            return newChat;
          });
        } else if (!aiResponse.includes("{")) {
          setChat((prev) => {
            const newChat = [...prev];
            newChat[newChat.length - 1] = { role: "ai", text: aiResponse };
            return newChat;
          });
        }
      },
      chatHistory,
    );

    // Parse AI response and extract structure (after streaming is complete)
    console.log("=== AIInputFloat: handleSend ===");
    console.log(
      "About to parse AI response, aiResponse length:",
      aiResponse.length,
    );
    console.log("Selected node ID:", selectedNodeId);
    console.log("Current nodes count:", nodes.length);

    // Use new text-based parser instead of JSON parser
    const parsedResponse = parseTextStructure(
      aiResponse,
      nodes,
      edges,
      selectedNodeId,
    );

    console.log("Parsed response:", {
      nodesCount: parsedResponse.nodes.length,
      edgesCount: parsedResponse.edges.length,
      parentNodeId: parsedResponse.parentNodeId,
      errors: parsedResponse.errors,
      isValid: parsedResponse.isValid,
    });

    // Update chat with the final conversational response
    setChat((prev) => {
      const newChat = [...prev];
      newChat[newChat.length - 1] = {
        role: "ai",
        text:
          parsedResponse.conversationalResponse ||
          "Response received and nodes added.",
      };
      return newChat;
    });

    // Process nodes or deletions
    const hasNodesToAdd = parsedResponse.nodes.length > 0;

    if (hasNodesToAdd) {
      let nodesWithPositions: Node<NodeData>[] = [];

      if (hasNodesToAdd) {
        console.log("Processing nodes for addition...");

        // Determine which nodes are new vs existing
        const existingNodeIds = new Set(nodes.map((n) => n.id));
        const newNodes = parsedResponse.nodes.filter(
          (n) => !existingNodeIds.has(n.id),
        );
        const existingNodesUpdates = parsedResponse.nodes.filter((n) =>
          existingNodeIds.has(n.id),
        );

        console.log(
          `Found ${newNodes.length} new nodes and ${existingNodesUpdates.length} node updates`,
        );

        // Calculate proper positions ONLY for new nodes
        // Check if one of the new nodes is a center node
        const newCenterNode = newNodes.find((n) => n.type === "center");

        // Find existing center node as fallback parent
        const existingCenterNode = nodes.find((n) => n.type === "center");
        const defaultParentId =
          parsedResponse.parentNodeId ||
          selectedNodeId ||
          existingCenterNode?.id ||
          "root";

        let nodesWithCalculatedPositions: Node<NodeData>[] = [];

        if (newNodes.length > 0) {
          // Get the child node distance from store (adjustable with Cmd+Plus/Minus)
          const childNodeDistance = useStore.getState().childNodeDistance;

          // Separate center node from other nodes
          const centerNodes = newNodes.filter((n) => n.type === "center");
          const regularNodes = newNodes.filter((n) => n.type !== "center");

          console.log(
            `Found ${centerNodes.length} center nodes and ${regularNodes.length} regular nodes`,
          );

          // For each regular node, determine its correct parentId
          nodesWithCalculatedPositions = regularNodes.map((node) => {
            // Check if this node's parentId points to the new center node
            let nodeParentId = node.parentId || defaultParentId;

            if (newCenterNode && node.parentId === newCenterNode.id) {
              // This node should connect to the new center node
              console.log(
                `Node ${node.data.label} connects to new center node ${newCenterNode.id}`,
              );
              nodeParentId = newCenterNode.id;
            } else if (!node.parentId) {
              // No explicit parentId, use default
              nodeParentId = defaultParentId;
            }

            return {
              ...node,
              parentId: nodeParentId,
            };
          });

          // Add center nodes without parentId
          const centerNodesWithoutParent = centerNodes.map((node) => {
            const { parentId, ...nodeWithoutParent } = node;
            return nodeWithoutParent as Node<NodeData>;
          });

          // Calculate positions for all nodes
          const allNewNodes = [
            ...centerNodesWithoutParent,
            ...nodesWithCalculatedPositions,
          ];

          // Group nodes by their parent for position calculation
          const nodesByParent = new Map<string, Node<NodeData>[]>();
          allNewNodes.forEach((node) => {
            const parent = node.parentId || "center";
            if (!nodesByParent.has(parent)) {
              nodesByParent.set(parent, []);
            }
            nodesByParent.get(parent)!.push(node);
          });

          // Calculate positions for each group
          const nodesWithPositions: Node<NodeData>[] = [];
          nodesByParent.forEach((nodeGroup, parent) => {
            const positions = calculateChildNodePositions(
              nodes,
              parent === "center" ? existingCenterNode?.id || "root" : parent,
              nodeGroup.length,
              childNodeDistance,
            );

            nodeGroup.forEach((node, i) => {
              nodesWithPositions.push({
                ...node,
                position: positions[i] || node.position || { x: 0, y: 0 },
              });
            });
          });

          nodesWithCalculatedPositions = nodesWithPositions;
          console.log(
            "Calculated positions for new nodes:",
            nodesWithCalculatedPositions,
          );
        }

        // For existing nodes, check if parentId is changing (move) or not (style update)
        // Only keep parentId if it's different from the current parentId (indicating a move)
        const cleanedExistingUpdates = existingNodesUpdates.map((node) => {
          const existingNode = nodes.find((n) => n.id === node.id);
          if (!existingNode) return node;

          // If parentId is changing, keep it (this is a move operation)
          if (node.parentId && node.parentId !== existingNode.parentId) {
            console.log(
              `Move detected: ${node.data.label} from ${existingNode.parentId} to ${node.parentId}`,
            );
            return node; // Keep the new parentId for move operations
          }

          // If parentId is the same or undefined, remove it (style update)
          const { parentId, ...nodeWithoutParent } = node;
          return nodeWithoutParent as Node<NodeData>;
        });

        nodesWithPositions = [
          ...nodesWithCalculatedPositions,
          ...cleanedExistingUpdates,
        ];

        console.log(
          "Nodes with positions:",
          nodesWithPositions.map((n) => ({
            id: n.id,
            label: n.data.label,
            parentId: n.parentId,
            isUpdate: existingNodeIds.has(n.id),
          })),
        );
      }

      // Notify parent component to add/move nodes
      if (onUpdateNodes) {
        console.log(
          "Calling onUpdateNodes with",
          nodesWithPositions.length,
          "nodes,",
          parsedResponse.edges.length,
          "edges",
        );
        onUpdateNodes(
          nodesWithPositions,
          parsedResponse.edges,
          [], // No deletions in text format
          null, // No selectNodeId in text format
          false, // No formatAll in text format
        );
      } else {
        console.warn("onUpdateNodes callback is not defined!");
      }
    } else {
      console.log("No nodes to add or delete");
    }
    console.log("=== End AIInputFloat: handleSend ===");

    setLoading(false);
    onSubmit(voiceText); // Notify parent if needed
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (inputText.trim() && !loading) {
        handleSend();
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  // Handle action buttons for notes context
  const handleAddToNotes = (action: "append" | "overwrite") => {
    if (pendingNotesContent && onNotesResponse) {
      onNotesResponse(pendingNotesContent, action);
      setChat((prev) => [
        ...prev,
        {
          role: "ai",
          text:
            action === "overwrite"
              ? "I've replaced your notes with the new content."
              : "I've added this to your notes.",
        },
      ]);
      setPendingNotesContent(null);
    }
  };

  const handleDismissPending = () => {
    setPendingNotesContent(null);
  };

  if (!visible) return null;

  const content = (
    <>
      <div
        className="ai-input-float"
        ref={containerRef}
        style={{
          left: position.x ? `${position.x}px` : "50%",
          top: position.y ? `${position.y}px` : "auto",
          transform: position.x ? "none" : "translateX(-50%)",
        }}
        onContextMenu={(e) => {
          // Stop propagation to prevent flow context menu
          e.stopPropagation();
          // Allow default browser context menu
        }}
      >
        <div className="ai-input-container" onMouseDown={handleMouseDown}>
          <div className="ai-input-drag-handle" onMouseDown={handleMouseDown}>
            <div className="ai-input-drag-indicator" />
          </div>

          {/* Chat history */}
          {chat.length > 0 && (
            <div className="ai-chat-history" ref={chatAreaRef}>
              {chat.map((msg, idx) => (
                <div
                  key={idx}
                  className={`ai-chat-message ai-chat-${msg.role}`}
                >
                  <div className="ai-chat-role">
                    {msg.role === "user" ? "You" : "AI"}
                  </div>
                  <div className="ai-chat-text">
                    {msg.role === "ai" ? (
                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                    ) : (
                      msg.text
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="ai-loading">
                  <ProgressSpinner
                    style={{ width: "20px", height: "20px" }}
                    strokeWidth="4"
                  />
                </div>
              )}
            </div>
          )}

          {/* Action buttons for notes context */}
          {context === "notes" && pendingNotesContent && !loading && (
            <div className="ai-action-buttons">
              <button
                className="ai-action-btn ai-action-append"
                onClick={() => handleAddToNotes("append")}
              >
                <i className="pi pi-plus" style={{ marginRight: "0.5rem" }} />
                Add to Notes
              </button>
              <button
                className="ai-action-btn ai-action-overwrite"
                onClick={() => handleAddToNotes("overwrite")}
              >
                <i className="pi pi-sync" style={{ marginRight: "0.5rem" }} />
                Replace Notes
              </button>
              <button
                className="ai-action-btn ai-action-dismiss"
                onClick={handleDismissPending}
              >
                <i className="pi pi-times" />
              </button>
            </div>
          )}

          <div className="ai-input-wrapper">
            <InputTextarea
              ref={textareaRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                context === "notes"
                  ? "Ask AI to help with your notes..."
                  : "What do you want to map? (experimental)"
              }
              rows={3}
              autoResize
              className="ai-input-textarea"
              disabled={loading}
            />
            <div
              className={`ai-input-icon ${recording ? "recording" : ""}`}
              onClick={toggleRecording}
              style={{ cursor: "pointer" }}
              title={recording ? "Stop recording" : "Start voice input"}
            >
              <BsMic size={24} color="#ffffff" />
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Render as portal to document.body to ensure it's above dialogs
  return ReactDOM.createPortal(content, document.body);
};

export default AIInputFloat;
